import React, { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "next-i18next";
import { useForm, SubmitHandler } from "react-hook-form";

import { Input } from "@atoms/input";

type FormData = {
  // step 1
  region: string;
  city: string;
  propertyType: string;
  propertyName: string;
  address: string;
  hasLicense: string;

  // step 2
  doubleRooms: number;
  singleBeds: number;
  bathrooms: number;
  size: number;
  distance: string;
  hasView: string;

  // step 3
  hasGarden: boolean;
  hasPool: boolean;
  hasFireplace: boolean;
  hasTerrace: boolean;
  hasParking: boolean;
  hasAirConditioning: boolean;
  hasHeating: boolean;
  hasWifi: boolean;
  hasBarbecue: boolean;
  hasWashingMachine: boolean;
  hasGym: boolean;
  hasBabyCrib: boolean;

  // step 4
  ownerName: string;
  phone: string;
  email: string;
  propertyUrl: string;
  notes: string;
};

export const HomeownersForm = () => {
  const { t } = useTranslation("homeowners");

  const steps = t("propertyForm.steps", { returnObjects: true }) as string[];

  const [step, setStep] = useState(0);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();

  const next = () => setStep((prev) => Math.min(prev + 1, steps.length - 1));
  const back = () => setStep((prev) => Math.max(prev - 1, 0));

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    const formattedBody = {
      message: `
  🏠 Nueva solicitud de propiedad recibida:
  
  📍 Información general
  - Región: ${data.region}
  - Ciudad: ${data.city}
  - Tipo de propiedad: ${data.propertyType}
  - Nombre: ${data.propertyName}
  - Dirección: ${data.address}
  - ¿Licencia turística?: ${data.hasLicense}
  
  📏 Características
  - Habitaciones dobles: ${data.doubleRooms}
  - Camas individuales: ${data.singleBeds}
  - Baños: ${data.bathrooms}
  - Superficie (m²): ${data.size}
  - Distancia al centro o parada: ${data.distance}
  - ¿Buenas vistas?: ${data.hasView}
  
  ✅ Extras seleccionados:
  - Jardín: ${data.hasGarden ? "✅" : "❌"}
  - Piscina: ${data.hasPool ? "✅" : "❌"}
  - Chimenea: ${data.hasFireplace ? "✅" : "❌"}
  - Terraza o balcón: ${data.hasTerrace ? "✅" : "❌"}
  - Parking: ${data.hasParking ? "✅" : "❌"}
  - Aire acondicionado: ${data.hasAirConditioning ? "✅" : "❌"}
  - Calefacción: ${data.hasHeating ? "✅" : "❌"}
  - Wifi: ${data.hasWifi ? "✅" : "❌"}
  - Barbacoa: ${data.hasBarbecue ? "✅" : "❌"}
  - Lavadora: ${data.hasWashingMachine ? "✅" : "❌"}
  - Gimnasio: ${data.hasGym ? "✅" : "❌"}
  - Cuna para bebé: ${data.hasBabyCrib ? "✅" : "❌"}
  
  👤 Información de contacto
  - Nombre: ${data.ownerName}
  - Teléfono: ${data.phone}
  - Email: ${data.email}
  - URL de la propiedad: ${data.propertyUrl}
  
  📝 Comentarios adicionales:
  ${data.notes || "—"}
      `,
    };

    try {
      const response = await fetch("https://formspree.io/f/xrbpyzvz", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formattedBody),
      });

      if (response.ok) {
        setStatus("success");
        setStep(4);
      } else {
        setStatus("error");
        setStep(4);
      }
    } catch (err) {
      setStatus("error");
      setStep(4);
      console.error(err);
    }
  };

  return (
    <Wrapper>
      <Header>{t("propertyForm.title")}</Header>
      <StepIndicator>
        {t("propertyForm.stepIndicator", {
          current: step + 1,
          total: steps.length + 1,
          label: steps[step],
        })}
      </StepIndicator>

      <form onSubmit={handleSubmit(onSubmit)}>
        <FormBody>
          {step === 0 && (
            <FormGroup>
              <Input type="text" placeholder={t("propertyForm.fields.region")} {...register("region")} />
              <Input type="text" placeholder={t("propertyForm.fields.city")} {...register("city")} />
              <Input type="text" placeholder={t("propertyForm.fields.propertyType")} {...register("propertyType")} />
              <Input type="text" placeholder={t("propertyForm.fields.propertyName")} {...register("propertyName")} />
              <Input type="text" placeholder={t("propertyForm.fields.address")} {...register("address")} />
              <Input type="text" placeholder={t("propertyForm.fields.hasLicense")} {...register("hasLicense")} />
            </FormGroup>
          )}

          {step === 1 && (
            <FormGroup>
              <Input type="number" placeholder={t("propertyForm.fields.doubleRooms")} {...register("doubleRooms")} />
              <Input type="number" placeholder={t("propertyForm.fields.singleBeds")} {...register("singleBeds")} />
              <Input type="number" placeholder={t("propertyForm.fields.bathrooms")} {...register("bathrooms")} />
              <Input type="number" placeholder={t("propertyForm.fields.size")} {...register("size")} />
              <Input type="text" placeholder={t("propertyForm.fields.distance")} {...register("distance")} />
              <Input type="text" placeholder={t("propertyForm.fields.hasView")} {...register("hasView")} />
            </FormGroup>
          )}

          {step === 2 && (
            <FormGroup>
              <CheckboxGroup>
                <label>
                  <input type="checkbox" {...register("hasGarden")} />
                  {t("propertyForm.fields.hasGarden")}
                </label>
                <label>
                  <input type="checkbox" {...register("hasPool")} />
                  {t("propertyForm.fields.hasPool")}
                </label>
                <label>
                  <input type="checkbox" {...register("hasFireplace")} />
                  {t("propertyForm.fields.hasFireplace")}
                </label>
                <label>
                  <input type="checkbox" {...register("hasTerrace")} />
                  {t("propertyForm.fields.hasTerrace")}
                </label>
                <label>
                  <input type="checkbox" {...register("hasParking")} />
                  {t("propertyForm.fields.hasParking")}
                </label>
                <label>
                  <input type="checkbox" {...register("hasAirConditioning")} />
                  {t("propertyForm.fields.hasAirConditioning")}
                </label>
                <label>
                  <input type="checkbox" {...register("hasHeating")} />
                  {t("propertyForm.fields.hasHeating")}
                </label>
                <label>
                  <input type="checkbox" {...register("hasWifi")} />
                  {t("propertyForm.fields.hasWifi")}
                </label>
                <label>
                  <input type="checkbox" {...register("hasBarbecue")} />
                  {t("propertyForm.fields.hasBarbecue")}
                </label>
                <label>
                  <input type="checkbox" {...register("hasWashingMachine")} />
                  {t("propertyForm.fields.hasWashingMachine")}
                </label>
                <label>
                  <input type="checkbox" {...register("hasGym")} />
                  {t("propertyForm.fields.hasGym")}
                </label>
                <label>
                  <input type="checkbox" {...register("hasBabyCrib")} />
                  {t("propertyForm.fields.hasBabyCrib")}
                </label>
              </CheckboxGroup>
            </FormGroup>
          )}

          {step === 3 && (
            <FormGroup>
              <Input type="text" placeholder={t("propertyForm.fields.ownerName")} {...register("ownerName")} />
              <Input type="tel" placeholder={t("propertyForm.fields.phone")} {...register("phone")} />
              <Input type="email" placeholder={t("propertyForm.fields.email")} {...register("email")} />
              <Input type="text" placeholder={t("propertyForm.fields.propertyUrl")} {...register("propertyUrl")} />
              <Input type="textarea" placeholder={t("propertyForm.fields.notes")} {...register("notes")} />
            </FormGroup>
          )}

          {step === 4 && (
            <FormGroup>
              {status === "success" ? (
                <>
                  <SuccessMessage>{t("propertyForm.submitSuccess.title")}</SuccessMessage>
                  <p>{t("propertyForm.submitSuccess.description")}</p>
                </>
              ) : (
                <>
                  <ErrorMessage>{t("propertyForm.submitError.title")}</ErrorMessage>
                  <p>{t("propertyForm.submitError.description")}</p>
                </>
              )}
            </FormGroup>
          )}
        </FormBody>

        <NavButtons>
          {step > 0 && (
            <Button type="button" onClick={back}>
              {t("propertyForm.buttons.back")}
            </Button>
          )}
          {step < steps.length - 1 && (
            <Button type="button" onClick={next}>
              {t("propertyForm.buttons.next")}
            </Button>
          )}
          {step === 3 && (
            <Button type="submit" primary>
              {t("propertyForm.buttons.submit")}
            </Button>
          )}
        </NavButtons>
      </form>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  background: #fff;
  padding: 32px 24px;
  max-width: 600px;
  margin: 0 auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  border-radius: 8px;
`;

const Header = styled.h2`
  margin-bottom: 16px;
  text-align: center;
`;

const StepIndicator = styled.div`
  margin-bottom: 24px;
  font-weight: bold;
  text-align: center;
`;

const FormBody = styled.div`
  margin-bottom: 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-self: center;
  gap: 12px;

  label {
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
`;

const NavButtons = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Button = styled.button<{ primary?: boolean }>`
  background: ${({ primary }) => (primary ? "#00a86b" : "#eee")};
  color: ${({ primary }) => (primary ? "white" : "#333")};
  border: none;
  border-radius: 6px;
  padding: 10px 20px;
  cursor: pointer;
  font-weight: bold;

  &:hover {
    opacity: 0.9;
  }
`;

const SuccessMessage = styled.h3`
  color: #00a86b;
  font-weight: bold;
`;

const ErrorMessage = styled.h3`
  color: #c53131;
  font-weight: bold;
`;
